import { and, i18nScope, not, option, or, plugin, role } from '../condition.js';
import type { ProductMenuDef } from '../type.js';

/**
 * Issues (Web / RightSupport) の L2 メニュー定義。
 * 出典: navigation-v5/src/constants/level2/issues.ts
 *
 * 出し分け条件は計画書 §7.2 (v5 parity 表) に準拠。
 * - L1 (Issues 自体): v5 は Support 系 / Connect 系で L1 が分岐するが、v2 は単一 product に集約する。
 *   表示は (`plugin:righttouch_rightsupport` または `plugin:righttouch_rightconnect` または
 *   `option:ISSUES/enable_righttouch_smart_agent_v2`)。
 *   i18n は L1 には付けない: Issues は Global版スコープ専用の L2 (CONTACT_POINTS 等, i18n=true) を持つため、
 *   L1 に `i18n(false)` を課すと Global版で製品ごと消えてしまう。i18n は各 L2 の condition に委ねる。
 *   (v5 の `!plugin:righttouch_qant_global_nav` 排他ゲートは v2 では省略。§7.4 注 参照)
 * - 基本: 各 L2 は `i18n(scope=false)` + `hasRole('ISSUES','view')`。
 *   差分のあるメニューのみ option / plugin / i18n を追加する (下記コメント参照)。
 *
 * `ISSUES_VIEW` = 基本条件 (i18n=false + role ISSUES.view) のショートハンド。
 */
const ISSUES_VIEW = and(i18nScope(false), role('ISSUES', 'view'));
/**
 * Web エージェント系。
 * 判定軸は現行ナビ (v5) と同じく契約 option `enable_righttouch_smart_agent_v2` 単独。
 * issues フロントは同じ機能をプラグイン `righttouch_smart_agent_v2` (`getIsPluginEnabled(...)`) で
 * 出し分けているが、plugin を OR で足すことはしない。理由は3点:
 * - karte-io-systems#156913 (2026-05-29) で現行ナビが plugin → ProductContract option へ移行した際に、
 *   本番全プロジェクトの `installed_plugins` と `productContracts.ISSUES` の差分が BQ 実測で 0 件と確認済み。
 *   「plugin のみ有効」なプロジェクトは実在しないので OR に救済対象がない。
 * - プラットフォーム方針として plugin は廃止し productContracts に寄せる方向 (karte-io-systems#156438 の
 *   レビュー指摘)。新パッケージに plugin 依存を持ち込むのは逆行になる。
 * - issues の契約変更ハンドラは option OFF 時にアクションを unpublish するが plugin は残るため、
 *   OR にすると解約済みプロジェクトにメニューが出続ける。
 */
const ISSUES_AGENT = and(i18nScope(false), role('ISSUES', 'view'), option('ISSUES', 'enable_righttouch_smart_agent_v2'));

const ISSUES_AI_ASSISTANT = and(role('ISSUES', 'view'), option('ISSUES', 'enable_qant_web_ai_assistant'));

export const QANT_ISSUES_MENU = {
  product: {
    name: 'issues',
    icon: 'qantWeb',
    label: { ja: 'Web', en: 'Web' },
    description: { ja: 'Web分析・施策', en: 'Web Analytics & Action' },
  },
  condition: or(plugin('righttouch_rightsupport'), plugin('righttouch_rightconnect'), option('ISSUES', 'enable_righttouch_smart_agent_v2')),
  menuGroups: {
    BASIC: {
      name: 'basic',
      label: { ja: '課題と効果をみる', en: 'Know issues and results' },
      menus: {
        DASHBOARD: {
          name: 'dashboard',
          icon: 'issuesDashboard',
          label: { ja: 'ダッシュボード', en: 'Dashboard' },
          path: { host: 'karte-io', path: '/rightsupport/' },
          condition: ISSUES_VIEW,
        },
        REPORT: {
          name: 'report',
          icon: 'issuesReport',
          label: { ja: 'レポート', en: 'Reports' },
          path: { host: 'karte-io', path: '/rightsupport/reports' },
          condition: ISSUES_VIEW,
        },
      },
    },
    SUPPORT: {
      name: 'support',
      label: { ja: 'ユーザーを助ける', en: 'Actions' },
      menus: {
        SUPPORT_ACTIONS: {
          name: 'supportActions',
          label: { ja: 'サポートアクション', en: 'Support actions' },
          icon: 'issuesAction',
          path: { host: 'karte-io', path: '/rightsupport/support-actions' },
          condition: ISSUES_VIEW,
        },
        AGENT_SETTINGS: {
          name: 'agentSettings',
          label: { ja: 'Webエージェント', en: 'Agent Settings' },
          icon: 'issuesAgent',
          path: { host: 'karte-io', path: '/rightsupport/agent-settings' },
          condition: ISSUES_AGENT,
        },
        SUPPORT_SITE: {
          name: 'supportSite',
          label: { ja: 'サポートサイト', en: 'Support Site' },
          icon: 'issuesSupportSite',
          path: { host: 'karte-io', path: '/rightsupport/support-site' },
          condition: and(i18nScope(false), role('ISSUES', 'view'), option('ISSUES', 'enable_righttouch_support_site')),
        },
      },
    },
    KNOWLEDGE: {
      name: 'knowledge',
      label: { ja: 'ナレッジ', en: 'Knowledge' },
      menus: {
        SCENARIOS: {
          name: 'scenarios',
          icon: 'issuesScenario',
          label: { ja: 'シナリオ', en: 'Scenarios' },
          path: { host: 'karte-io', path: '/rightsupport/scenarios' },
          condition: ISSUES_VIEW,
        },
        ANSWERS: {
          name: 'answers',
          icon: 'issuesFaq',
          label: { ja: 'アンサー', en: 'Answers' },
          path: { host: 'karte-io', path: '/rightsupport/faqs' },
          condition: ISSUES_VIEW,
        },
      },
    },
    CONNECT: {
      name: 'connect',
      label: { ja: 'オペレータ向け', en: 'For Operator' },
      menus: {
        CONNECT_BOARD: {
          name: 'connectBoard',
          icon: 'issuesConnectBoard',
          label: { ja: 'コネクトボード', en: 'Connect Board' },
          path: { host: 'karte-io', path: '/rightsupport/connect/connect-board' },
          condition: and(i18nScope(false), role('RIGHTCONNECT', 'view'), option('ISSUES', 'enable_righttouch_rightconnect')),
        },
      },
    },
    OTHER: {
      name: 'other',
      label: { ja: 'その他の機能', en: 'Others' },
      menus: {
        AI_ASSISTANT: {
          name: 'aiAssistant',
          icon: 'neuroAiAssistant',
          label: { ja: 'AIアシスタント', en: 'AI Assistant' },
          path: { host: 'karte-io', path: '/rightsupport/qwaias' },
          condition: ISSUES_AI_ASSISTANT,
        },
        OTHER: {
          icon: 'other',
          name: 'other',
          label: { ja: 'その他の機能', en: 'Others' },
          popoverMenus: {
            BASIC: {
              name: 'basic',
              items: {
                REASONS: {
                  name: 'reasons',
                  label: { ja: 'リーズン', en: 'Reasons' },
                  path: { host: 'karte-io', path: '/rightsupport/reasons' },
                  condition: ISSUES_VIEW,
                },
                CHECKPOINTS: {
                  name: 'checkpoints',
                  label: { ja: 'チェックポイント', en: 'Checkpoint' },
                  path: { host: 'karte-io', path: '/rightsupport/checkpoints' },
                  condition: ISSUES_VIEW,
                },
                FORM: {
                  name: 'form',
                  label: { ja: 'フォーム', en: 'Form' },
                  path: { host: 'karte-io', path: '/rightsupport/forms' },
                  condition: ISSUES_VIEW,
                },
                CONTACT_POINTS: {
                  name: 'contactPoints',
                  label: { ja: '問い合わせ窓口', en: 'Contact Points' },
                  path: { host: 'karte-io', path: '/rightsupport/contact-points' },
                  // §7.2: i18n(scope=true) + hasRole(ISSUES,view)
                  condition: and(i18nScope(true), role('ISSUES', 'view')),
                },
                BRAND_SETTINGS: {
                  name: 'brandSettings',
                  label: { ja: 'ブランド設定', en: 'Brand Settings' },
                  path: { host: 'karte-io', path: '/rightsupport/brands' },
                  condition: and(i18nScope(true), role('ISSUES', 'view')),
                },
                SMS_SETTINGS: {
                  name: 'smsSettings',
                  label: { ja: 'SMS設定', en: 'SMS Settings' },
                  path: { host: 'karte-io', path: '/rightsupport/sms' },
                  // 計画書 §7.2 の表は role を落としていたが、v5 実装 (level2/issues.ts) は
                  // CONTACT_POINTS / BRAND_SETTINGS と同じく hasRole(ISSUES,view) も課している。
                  condition: and(i18nScope(true), role('ISSUES', 'view'), option('ISSUES', 'enable_righttouch_sms')),
                },
                OPERATOR_ACTION_TEMPLATES: {
                  name: 'operatorActionTemplates',
                  label: { ja: 'オペレーターアクション定型文', en: 'Operator Action Templates' },
                  path: { host: 'karte-io', path: '/rightsupport/connect/operator-action-templates' },
                  // §7.2: i18n(true) + hasRole(RIGHTCONNECT,view) + option enable_righttouch_operator_action
                  condition: and(i18nScope(true), role('RIGHTCONNECT', 'view'), option('ISSUES', 'enable_righttouch_operator_action')),
                },
              },
            },
            WEB_AGENT: {
              name: 'webAgent',
              label: { ja: 'Webエージェント共通設定', en: 'Shared Web Agent Settings' },
              items: {
                AGENT_LOG: {
                  name: 'agentLog',
                  label: { ja: '応対ログ', en: 'Agent Log' },
                  path: { host: 'karte-io', path: '/rightsupport/agent-log' },
                  condition: ISSUES_AGENT,
                },
                REASON_TAGS: {
                  name: 'reasonTags',
                  label: { ja: 'リーズンタグ', en: 'Reason Tags' },
                  path: { host: 'karte-io', path: '/rightsupport/reason-tags' },
                  condition: ISSUES_AGENT,
                },
                TOOL: {
                  name: 'tool',
                  label: { ja: '応答パターン', en: 'Tool' },
                  path: { host: 'karte-io', path: '/rightsupport/tools' },
                  condition: ISSUES_AGENT,
                },
                AGENT_TEST: {
                  name: 'agentTest',
                  label: { ja: 'エージェント自動検証設定', en: 'Agent Test' },
                  path: { host: 'karte-io', path: '/rightsupport/agent-test' },
                  condition: ISSUES_AGENT,
                },
                SMART_BANNER: {
                  name: 'smartBanner',
                  label: { ja: '入力例', en: 'Input Examples' },
                  path: { host: 'karte-io', path: '/rightsupport/smart-banner' },
                  condition: ISSUES_AGENT,
                },
                TABULA_TABLE: {
                  name: 'tabulaTable',
                  label: { ja: 'リソーステーブル', en: 'Resource Table' },
                  path: { host: 'karte-io', path: '/rightsupport/product-tables' },
                  // §7.2: + option enable_righttouch_tabula_table
                  condition: and(i18nScope(false), role('ISSUES', 'view'), option('ISSUES', 'enable_righttouch_tabula_table')),
                },
              },
            },
            AI_ASSISTANT: {
              name: 'aiAssistant',
              label: { ja: 'AIアシスタント機能・設定', en: 'AI Assistant' },
              items: {
                SKILLS: {
                  name: 'aiAssistantSkills',
                  label: { ja: 'スキル実行', en: 'Skills' },
                  path: { host: 'karte-io', path: '/rightsupport/settings/ai-assistant/skills' },
                  condition: ISSUES_AI_ASSISTANT,
                },
                SETTINGS: {
                  name: 'aiAssistantSettings',
                  label: { ja: 'AIアシスタント設定', en: 'AI Assistant Settings' },
                  path: { host: 'karte-io', path: '/rightsupport/settings/ai-assistant' },
                  condition: ISSUES_AI_ASSISTANT,
                },
              },
            },
            KARTE: {
              name: 'karte',
              label: { ja: 'KARTEの機能', en: 'Karte Features' },
              // KARTE 本体の機能ページは `?project=` を解釈せず `/p/<projectId>/...` のみを受け付けるため
              // `projectIdIn: 'path'` を付ける (KARTE 現行ナビも getAdminProjectUrl ではなく getProjectUrl を使う)。
              //
              // 遷移先は KARTE 本体のナビに切り替わり QANT のグローバルナビが消えるため、同一タブだと
              // 元の画面へ戻る導線がない。QANT_KARTE_MENU (KARTE プロダクトカード) と同じく新規タブで開く。
              items: {
                KARTE_EVENT: {
                  name: 'karteEvent',
                  label: { ja: 'イベント', en: 'Event' },
                  path: { host: 'karte-io', path: '/event_settings', projectIdIn: 'path' },
                  options: { openAnotherTab: true },
                  condition: ISSUES_VIEW,
                },
                KARTE_SEGMENT: {
                  name: 'karteSegment',
                  label: { ja: 'セグメント', en: 'Segment' },
                  path: { host: 'karte-io', path: '/segment', projectIdIn: 'path' },
                  options: { openAnotherTab: true },
                  // §7.2: + !hasInstalledPlugin('ingest')
                  condition: and(i18nScope(false), role('ISSUES', 'view'), not(plugin('ingest'))),
                },
              },
            },
            SETTINGS: {
              name: 'settings',
              label: { ja: '設定', en: 'Settings' },
              items: {
                SETTINGS: {
                  name: 'settings',
                  label: { ja: 'その他の設定', en: 'Other Settings' },
                  path: { host: 'karte-io', path: '/rightsupport/settings' },
                  condition: ISSUES_VIEW,
                },
              },
            },
          },
        },
      },
    },
  },
} as const satisfies ProductMenuDef;
